import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faBuilding } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

// ─────────────────────────────────────────────────────────────────────────────
//  Bureau detail — is this matchmaker actually working?
//
//  The intake funnel is the answer, and it is the one funnel this whole phase
//  exists to prove: she adds a family, the family fills the form, a profile
//  appears in the pool. A bureau with twelve invites and zero confirmations is
//  a phone call, not a dashboard number.
//
//  CONTACT LOCK: no client phone number appears on this screen, because the
//  server never sends one. Admin can see that a family is in her book and how
//  far along they are — never how to ring them. Her own business line is public
//  by design and is shown.
// ─────────────────────────────────────────────────────────────────────────────

const FUNNEL_ORDER = ["INVITED", "OPENED", "PARTIAL", "CONFIRMED", "DECLINED", "EXPIRED", "WITHDRAWN"];
const FUNNEL_LABEL = {
  INVITED: "Sent", OPENED: "Opened", PARTIAL: "Started", CONFIRMED: "Joined",
  DECLINED: "Declined", EXPIRED: "Expired", WITHDRAWN: "Closed",
};
const STATUS_LABEL = { active: "Active", stale: "Quiet", engaged: "Engaged", married: "Married" };

const Stat = ({ label, value, tone }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm">
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${tone || "text-gray-800"}`}>{value}</div>
  </div>
);

const ViewBureau = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await func.get(`${urls.GET_PARTNER_BY_ID_URL}${id}`);
        if (res?.status === 200) setData(res.data);
        else toast.error("Could not load this bureau");
      } catch (e) {
        toast.error("Network error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) return <div className="p-6 text-gray-400">Loading…</div>;
  if (!data?.partner) return <div className="p-6 text-gray-500">Bureau not found.</div>;

  const p = data.partner;
  const book = data.book || {};
  const funnel = data.intake?.funnel || {};

  return (
    <div className="p-4 sm:p-6">
      <button onClick={() => navigate("/dashboard/bureau/list")}
        className="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-2">
        <FontAwesomeIcon icon={faArrowLeft} /> Back to bureaus
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faBuilding} className="text-amber-600" />
            {p.bureauName || p.displayName}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {p.displayName} · /@{p.slug}
            {p.publicBusinessPhone ? ` · ${p.publicBusinessPhone}` : ""}
            {data.account?.phone ? ` · login ${data.account.phone}` : ""}
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{p.hostStatus}</span>
            <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">KYC {p.kycStatus}</span>
            {(p.communitiesServed || []).slice(0, 4).map((c) => (
              <span key={c} className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700">{c}</span>
            ))}
          </div>
        </div>
      </div>

      {data.referredBy && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 text-sm text-amber-800">
          Referred by <b>{data.referredBy.referrerName || "a guest"}</b>
          {" "}({data.referredBy.relationship}) · referral status {data.referredBy.status}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Clients" value={book.clients ?? 0} />
        <Stat label="Brides / Grooms" value={`${book.female ?? 0} / ${book.male ?? 0}`} />
        <Stat label="Complete profiles" value={book.complete ?? 0} />
        <Stat
          label="Balance"
          tone={book.need ? "text-amber-700" : "text-green-700"}
          value={book.need
            ? `+${book.need.count} ${book.need.gender === "Male" ? "grooms" : "brides"}`
            : (book.clients ? "Ready" : "—")}
        />
      </div>

      <h2 className="text-sm font-semibold text-gray-700 mb-2">Intake funnel</h2>
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          {FUNNEL_ORDER.map((k) => (
            <div key={k} className="min-w-[84px]">
              <div className="text-xl font-semibold text-gray-800">{funnel[k] || 0}</div>
              <div className="text-xs text-gray-500">{FUNNEL_LABEL[k]}</div>
            </div>
          ))}
        </div>
        {!Object.keys(funnel).length && (
          <p className="text-sm text-gray-400">She has not sent a form yet.</p>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Her clients</h2>
          <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Gender</th>
                  <th className="text-left px-4 py-2">Profile</th>
                  <th className="text-left px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(book.rows || []).map((c) => (
                  <tr key={c._id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-800">
                      {c.name}
                      {c.blank && <span className="ml-2 text-[10px] text-gray-400">masked</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{c.gender}</td>
                    <td className="px-4 py-2">
                      {c.complete
                        ? <span className="text-green-700 text-xs">Complete</span>
                        : <span className="text-amber-700 text-xs">Step {c.onboardingStep ?? 0}/16</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-600 text-xs">{STATUS_LABEL[c.status] || c.status}</td>
                  </tr>
                ))}
                {!(book.rows || []).length && (
                  <tr><td colSpan="4" className="px-4 py-6 text-center text-gray-400">No clients yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Forms sent</h2>
          <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">State</th>
                  <th className="text-left px-4 py-2">Reached</th>
                  <th className="text-left px-4 py-2">Sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data.intake?.rows || []).map((i) => (
                  <tr key={i._id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-800">{i.name}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">{FUNNEL_LABEL[i.state] || i.state}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">
                      {i.state === "CONFIRMED" ? "6/6" : `${i.lastStep || 0}/6`}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {i.invitedAt ? new Date(i.invitedAt).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
                {!(data.intake?.rows || []).length && (
                  <tr><td colSpan="4" className="px-4 py-6 text-center text-gray-400">No forms sent yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBureau;
